import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Cookie, Database, UserCheck, Mail, Server, Timer, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MobileCollapse } from '../components/MobileCollapse';
import { PageBreadcrumb } from '../components/PageBreadcrumb';

/**
 * Politique de confidentialité — RGPD (UE 2016/679) + loi Informatique & Libertés.
 * Sections repliables sur mobile (MobileCollapse), visibles sur desktop.
 */
export default function Confidentialite() {
  useEffect(() => {
    document.title = 'Politique de confidentialité — CODE 2 ROUES';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        "Politique de confidentialité Code 2 Roues : données collectées, finalités, durées de conservation, droits RGPD. Plateforme d'entraînement au code moto, à but non lucratif."
      );
    }
  }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-4 md:space-y-6" data-testid="confidentialite-page">
      <PageBreadcrumb items={[{ label: 'Confidentialité' }]} />
      <div className="flex items-center gap-2">
        <Link to="/" data-testid="confidentialite-back">
          <Button variant="ghost" size="sm" className="-ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour à l'accueil
          </Button>
        </Link>
      </div>

      <section className="mc-card relative overflow-hidden p-5 sm:p-8" data-testid="confidentialite-hero">
        <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20" style={{ background: 'hsl(var(--primary))' }} />
        <div className="relative">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1 inline-flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Données personnelles
          </div>
          <h1 className="text-2xl sm:text-4xl font-black tracking-tight">Politique de confidentialité</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Conformément au RGPD (UE 2016/679) et à la loi Informatique et Libertés du 6 janvier 1978 modifiée.
          </p>
          <p className="text-[11px] text-muted-foreground mt-2">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Responsable de traitement — toujours visible (pivot RGPD) */}
      <section className="mc-card p-5 sm:p-6" data-testid="confidentialite-controller">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl grid place-items-center bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
            <UserCheck className="w-5 h-5" />
          </div>
          <h2 className="text-lg sm:text-xl font-black tracking-tight">1. Responsable de traitement</h2>
        </div>
        <p className="text-sm leading-relaxed text-foreground/85">
          Le responsable du traitement des données personnelles collectées sur Code 2 Roues est
          <strong> Bruno LLORENS</strong>, Enseignant de la Conduite et de la Sécurité Routière (ECSR)
          — exploitant la plateforme sous l'identité éditoriale <strong>« LLB ECSR »</strong> —
          exerçant à Bourgoin-Jallieu, Nord Isère (38).
          Contact : <a href="mailto:contact@code2roues.fr" className="text-[hsl(var(--primary))] font-bold hover:underline">contact@code2roues.fr</a>.
        </p>
      </section>

      {/* Sections repliables */}
      <div className="mc-card p-4 sm:p-6">
        <MobileCollapse title="2. Données collectées & finalités" icon={Database} level="h2" testId="confidentialite-data">
          <div className="space-y-3 text-sm leading-relaxed text-foreground/85">
            <div>
              <p className="font-bold mb-1">a) Données de compte (candidat ou enseignant)</p>
              <ul className="list-none pl-0 space-y-1">
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span> Email, mot de passe (haché bcrypt), prénom, rôle (élève / enseignant / admin)</li>
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><strong>Finalité :</strong> création et gestion du compte, authentification</li>
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><strong>Base légale :</strong> exécution du contrat (CGU)</li>
              </ul>
            </div>
            <div>
              <p className="font-bold mb-1">b) Données de progression pédagogique</p>
              <ul className="list-none pl-0 space-y-1">
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span> Réponses aux questions, scores aux examens blancs, modules complétés, sessions de révision</li>
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><strong>Finalité :</strong> personnalisation de l'apprentissage, suivi de la progression</li>
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><strong>Base légale :</strong> intérêt légitime du candidat</li>
              </ul>
            </div>
            <div>
              <p className="font-bold mb-1">c) Mesure d'audience anonymisée (PostHog)</p>
              <ul className="list-none pl-0 space-y-1">
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span> Pages visitées, parcours utilisateur, durée de session, type d'appareil</li>
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><strong>Finalité :</strong> amélioration de l'ergonomie et détection des bugs</li>
                <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><strong>Base légale :</strong> intérêt légitime, données pseudonymisées</li>
              </ul>
            </div>
          </div>
        </MobileCollapse>

        <MobileCollapse title="3. Sous-traitants & transferts" icon={Server} level="h2" testId="confidentialite-subprocessors">
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left bg-muted/40">
                  <th className="px-3 py-2 font-black text-[11px] uppercase tracking-widest">Sous-traitant</th>
                  <th className="px-3 py-2 font-black text-[11px] uppercase tracking-widest">Finalité</th>
                  <th className="px-3 py-2 font-black text-[11px] uppercase tracking-widest">Localisation</th>
                </tr>
              </thead>
              <tbody className="text-foreground/85">
                <tr className="border-t border-border">
                  <td className="px-3 py-2 font-bold">Resend</td>
                  <td className="px-3 py-2">Envoi des emails transactionnels</td>
                  <td className="px-3 py-2">USA (CCT)</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3 py-2 font-bold">OpenAI (via Emergent)</td>
                  <td className="px-3 py-2">Coach IA pédagogique (GPT-4o-mini)</td>
                  <td className="px-3 py-2">USA (CCT)</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3 py-2 font-bold">PostHog</td>
                  <td className="px-3 py-2">Mesure d'audience anonymisée</td>
                  <td className="px-3 py-2">USA (CCT)</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3 py-2 font-bold">OVH SAS</td>
                  <td className="px-3 py-2">Enregistrement du nom de domaine</td>
                  <td className="px-3 py-2">France (UE)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground italic mt-3">
            Aucune donnée n'est revendue à des tiers. Aucun profilage publicitaire.
          </p>
        </MobileCollapse>

        <MobileCollapse title="4. Durées de conservation" icon={Timer} level="h2" testId="confidentialite-retention">
          <ul className="list-none pl-0 space-y-1.5 text-sm text-foreground/85">
            <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><span><strong>Compte actif :</strong> tant qu'utilisé. Suppression auto après 24 mois d'inactivité.</span></li>
            <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><span><strong>Données de progression :</strong> liées à la durée de vie du compte.</span></li>
            <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><span><strong>Logs techniques :</strong> 12 mois maximum.</span></li>
            <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><span><strong>Mesure PostHog :</strong> 13 mois maximum (recommandation CNIL).</span></li>
          </ul>
        </MobileCollapse>

        <MobileCollapse title="5. Vos droits RGPD" icon={Scale} level="h2" testId="confidentialite-rights" defaultOpen>
          <p className="text-sm leading-relaxed text-foreground/85 mb-2">
            Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :
          </p>
          <ul className="list-none pl-0 space-y-1.5 text-sm text-foreground/85">
            <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span> Droit d'accès, de rectification et d'effacement (« droit à l'oubli »)</li>
            <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span> Droit à la limitation et à la portabilité du traitement</li>
            <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span> Droit d'opposition au traitement, notamment la mesure d'audience</li>
            <li className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span> Droit d'introduire une réclamation auprès de la <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noopener noreferrer" className="underline hover:text-[hsl(var(--primary))]">CNIL</a></li>
          </ul>
          <div className="mt-3 p-3 rounded-lg bg-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/15 flex items-start gap-2 text-sm">
            <Mail className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
            <span>
              Pour exercer vos droits : <a href="mailto:contact@code2roues.fr" className="font-bold text-[hsl(var(--primary))] hover:underline">contact@code2roues.fr</a> — réponse sous 30 jours.
            </span>
          </div>
        </MobileCollapse>

        <MobileCollapse title="6. Cookies" icon={Cookie} level="h2" testId="confidentialite-cookies">
          <div className="space-y-2 text-sm leading-relaxed text-foreground/85">
            <p>
              Code 2 Roues utilise uniquement des cookies <strong>strictement nécessaires</strong> au fonctionnement
              (jeton de session JWT, préférence de thème clair/sombre) et un outil de mesure d'audience pseudonymisée
              (PostHog), exempté de consentement préalable selon les recommandations de la CNIL.
            </p>
            <p>
              Aucun cookie publicitaire ni de profilage tiers n'est déposé.
            </p>
          </div>
        </MobileCollapse>
      </div>

      <div className="text-center text-xs text-muted-foreground py-4">
        © {new Date().getFullYear()} Bruno LLORENS (LLB ECSR) — Code 2 Roues. Tous droits réservés.
      </div>
    </div>
  );
}
